import React from 'react'
import { PrismicRichText } from '@prismicio/react'
import {
  PrismicLinkType,
  PrismicStructuredTextType,
} from 'src/typings/generated/graphql'
import { generateRandomId } from 'src/utils/domHelper'
import CustomLink from 'src/utils/customLink'

import * as GlobalStyles from 'src/styles/global.module.scss'

import * as Styles from './contactColumns.module.scss'

export type ContactColumnLineItem = {
  title?: string
  description?: PrismicStructuredTextType
  buttonLabel?: string
  buttonLink?: PrismicLinkType
}

export type ContactColumn = {
  title?: string
  items: ContactColumnLineItem[]
  buttonText?: string
  buttonLink?: PrismicLinkType
}

export type ContactColumnsContent = {
  anchor?: string
  headline?: string
  columns: ContactColumn[]
}

type Props = {
  content: ContactColumnsContent
}

const ContactColumns = ({ content }: Props) => {
  const { anchor = generateRandomId(), headline, columns } = content

  if (!columns || columns.length === 0) {
    return null
  }

  return (
    <section id={anchor} className={Styles.contactColumns}>
      <div className={Styles.inner}>
        {headline && <h2 className={Styles.headline}>{headline}</h2>}
        <div className={Styles.grid}>
          {columns.map(column => (
            <div
              key={`contact-column-${column.title ?? ''}-${
                column.buttonText ?? ''
              }`}
              className={Styles.column}
            >
              {column.title && (
                <h3 className={Styles.columnTitle}>{column.title}</h3>
              )}
              <div className={Styles.box}>
                <div className={Styles.body}>
                  {column.items.map(item => {
                    const hasDescription = Boolean(
                      item.description?.richText?.some((node: any) =>
                        node?.text?.trim()
                      )
                    )

                    return (
                      <div
                        key={`contact-line-item-${item.title ?? ''}-${
                          item.buttonLabel ?? ''
                        }`}
                        className={`${Styles.lineItem} ${
                          hasDescription ? '' : Styles.lineItemCentered
                        }`}
                      >
                        <div className={Styles.lineItemText}>
                          {item.title && (
                            <p className={Styles.lineItemTitle}>{item.title}</p>
                          )}
                          {hasDescription && (
                            <div className={Styles.lineItemDescription}>
                              <PrismicRichText
                                field={item.description?.richText}
                              />
                            </div>
                          )}
                        </div>
                        {item.buttonLink?.url && (
                          <CustomLink
                            className={`${GlobalStyles.redGhostButtonResponsive} ${GlobalStyles.small} ${Styles.lineItemButton}`}
                            link={item.buttonLink}
                            ariaLabel={`${item.buttonLabel ?? ''} ${
                              item.title ?? ''
                            }`}
                          >
                            {item.buttonLabel ?? ''}
                          </CustomLink>
                        )}
                      </div>
                    )
                  })}
                </div>
                {column.buttonLink?.url && (
                  <CustomLink
                    className={`${GlobalStyles.redButtonResponsive} ${GlobalStyles.small} ${Styles.button}`}
                    link={column.buttonLink}
                    ariaLabel={`${column.buttonText ?? ''} ${
                      column.title ?? ''
                    }`}
                  >
                    {column.buttonText ?? ''}
                  </CustomLink>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default ContactColumns
